import type {
    InventoryChangeEntry,
    InventoryRow,
    InventoryRowEdit,
    PermissionRole,
    Pool,
    PoolManagementModel,
    PoolPermission,
    PoolPermissionCandidate,
    PoolPermissionHolder,
    PoolManagementView,
    PoolStatus,
} from '.';
import {
    clearRowFieldEdit,
    clearStagedChanges,
    clearSubmitError,
    closeSubmitReview,
    closeUploadWizard,
    failSubmit,
    failUploadApply,
    initialPoolManagementState,
    openPoolDetail,
    openPoolList,
    openSubmitReview,
    openUploadWizard,
    poolManagementReducer,
    resetEdits,
    setActivePoolDetailTab,
    setAddSymbolDraft,
    setAppliedSymbolSearch,
    setChangeLogActionFilter,
    setChangeLogSymbolFilter,
    setChangeLogTimeRange,
    setInventoryPage,
    setInventoryRowsPerPage,
    setPoolSearchQuery,
    setSymbolSearchQuery,
    setUploadCategoryFilter,
    setUploadError,
    setUploadFile,
    setUploadMode,
    setUploadStep,
    setUploadSymbolFilter,
    stageRowEdit,
    stageRowRemoval,
    startSubmit,
    startUploadApply,
    succeedSubmit,
    succeedUploadApply,
    unstageRowRemoval,
} from './state';
import type {
    ChangeLogActionFilter,
    ChangeLogTimeRange,
    InventoryRowsPerPage,
    PoolDetailTab,
    PoolManagementAction,
    PoolManagementState,
    UploadCategory,
    UploadFileInfo,
    UploadMode,
    UploadWizardStep,
} from './state';
import { computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { QueryClient, QueryObserverOptions } from '@tanstack/query-core';
import type {
    PermissionDirective,
    PermissionType,
    PermissionUpdateEntry,
} from '@thinkalpha/common/contracts/permissions.js';
import { isSpecialPermissionSubject, isTypedPermissionDirective } from '@thinkalpha/common/contracts/permissions.js';
import type {
    ChangeLogEntry as ApiChangeLogEntry,
    GetPoolChangeLogResponse,
    InventoryItem,
    ListPoolItemsResponse,
    NewPoolItem,
} from '@thinkalpha/locates-ws-client';
import type { FirmMembershipWithProfile } from '@thinkalpha/ui-types/types/admin/firms';
import type { InventoryPool, ListPoolsResponse } from '@thinkalpha/ui-types/types/admin/lab-pools';
import { t } from 'i18next';
import debounce from 'lodash.debounce';
import { createChangeLogCsv } from 'src/admin/LAB/PoolManagement/poolChangeLogCsv';
import { diffInventory } from 'src/admin/models/PoolManagementModel/upload/diffInventory';
import type { UploadDiff } from 'src/admin/models/PoolManagementModel/upload/diffInventory';
import {
    createInventoryCsv,
    createInventoryTemplateCsv,
    parseInventoryCsv,
} from 'src/admin/models/PoolManagementModel/upload/parseInventoryCsv';
import type { InventoryParseResult } from 'src/admin/models/PoolManagementModel/upload/parseInventoryCsv';
import { inject, injectable } from 'src/features/ioc';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getFirmMembersQuery } from 'src/queries/firms';
import {
    getAllPoolItemsQuery,
    getAllPoolsQuery,
    getFirmPoolsQuery,
    getPoolChangeLogQuery,
    getPoolItemsQuery,
    invalidatePoolChangeLogQuery,
    invalidatePoolItemsQuery,
    invalidatePoolsQuery,
    LAB_POOLS,
} from 'src/queries/labPools';
import {
    addPoolItems,
    removePoolItem,
    updatePool,
    updatePoolItem,
    updatePoolPermissionsForEntity,
} from 'src/queries/labPoolsMutations';
import type { ReactBindings } from 'src/types/bindings';

const PENDING_ROW_PREFIX = '__pending__';

/**
 * `true` when the row id was minted locally for a not-yet-submitted addition.
 * Exposed so views can render draft rows with distinct visual treatment.
 */
export const isDraftRowId = (id: string): boolean => id.startsWith(PENDING_ROW_PREFIX);

/**
 * One leg of the fan-out submit. `kind` identifies what staged change
 * the op represents so the post-`Promise.allSettled` walk can clear
 * exactly the succeeded ones (leaving failures staged for retry).
 */
type SubmitOperation =
    | { kind: 'update'; rowId: string; promise: Promise<unknown> }
    | { kind: 'remove'; rowId: string; promise: Promise<unknown> }
    /**
     * Carries the pending-row ids that participated in the batch so a
     * successful POST can clear their staged `rowEdits` entries alongside
     * dropping them from `#pendingAddsByPool`. Without this, edits keyed
     * by `PENDING_ROW_PREFIX...` would be orphaned in `rowEdits` after a
     * partial-failure retry and leave the model invisibly dirty.
     */
    | { kind: 'add-batch'; rowIds: string[]; promise: Promise<unknown> };

/**
 * Upload size cap (8 MiB). The parser is fully synchronous and runs on
 * the main thread, so an unbounded `file.text()` on a several-hundred-
 * megabyte CSV would lock up the UI. 8 MiB is generous for the
 * 10k-row CSV the wizard advertises (~700 KB typical) and still small
 * enough to parse without measurable jank.
 */
const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

/**
 * Debounce window before a symbol-search keystroke is committed to the
 * backend `symbol` query parameter. Keeps live typing to a single
 * request once the user pauses rather than one exact-match request per
 * keystroke.
 */
const SYMBOL_SEARCH_DEBOUNCE_MS = 300;

/**
 * First-page size for the change-log tab. The backend exposes
 * `skip` / `limit` but the current UI has no pager — picking a
 * generous first page keeps the timeline useful without a paging
 * affordance. The pageable plumbing is in place so we can add
 * "load more" later without re-keying the query.
 */
const CHANGE_LOG_PAGE_SIZE = 200;

/**
 * Collapse whitespace and strip filesystem-reserved + control
 * characters from a string so it's safe to drop into a CSV download
 * filename. Windows is the strictest target (`<>:"/\|?*`), so we
 * sanitize for that set and let the cross-platform underscore
 * fallback do the rest. We also trim trailing dots/spaces and prefix
 * an underscore on Windows reserved device names (CON, PRN, AUX, NUL,
 * COM1..9, LPT1..9), both of which produce unusable filenames there.
 */
function sanitizeFilenameSegment(name: string): string {
    const sanitized = name
        .trim()
        .replace(/[<>:"/\\|?*\x00-\x1F]/g, '_')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/[. ]+$/, '');
    return /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i.test(sanitized) ? `_${sanitized}` : sanitized;
}

/**
 * Provisional mapping from the SQL Journal `act` integer to the UI's
 * {@link ChangeLogActionBucket}. The backend ships the raw integer
 * with the contract noted as "untested, subject to change", so we
 * cluster by likely intent and fall back to `'other'` for anything
 * unrecognized. Source guesses: ModifyLocate.sql writes `act=2` for
 * locate apply; `1` likely creates; `3`/`4` likely a removal or
 * adjustment. Update once backend docs the mapping.
 */
const ACTION_BUCKET_BY_CODE = new Map<number, 'added' | 'updated' | 'removed' | 'locate'>([
    [1, 'added'],
    [2, 'locate'],
    [3, 'removed'],
    [4, 'updated'],
]);

function timeRangeCutoffMs(range: ChangeLogTimeRange): number | null {
    if (range === 'custom') return null;
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    if (range === 'today') return start.getTime();
    const days = range === 'last-7-days' ? 7 : 30;
    // Subtract `days - 1` so "last 7 days" covers today plus the previous
    // six calendar days, matching typical user mental model.
    start.setDate(start.getDate() - (days - 1));
    return start.getTime();
}

function fromApiChangeLogEntry(entry: ApiChangeLogEntry): InventoryChangeEntry {
    const ms = Date.parse(entry.timestamp);
    const userId = entry.userId;
    return {
        id: entry.id,
        timestamp: Number.isFinite(ms) ? ms : 0,
        userId,
        userName: userId ?? 'System',
        orderId: entry.orderId,
        action: entry.action,
        bucket: ACTION_BUCKET_BY_CODE.get(entry.action) ?? 'other',
        symbol: entry.symbol,
        quantity: entry.quantity,
        price: entry.price,
    };
}

/**
 * The backend's `PermissionType` is the four-rung ladder
 * `reader | writer | owner | admin`. The pool UI only cares about
 * the read/write/own gating, so admin collapses to `owner`.
 */
function fromEffectiveAccess(access: PermissionType): PoolPermission {
    return access === 'admin' ? 'owner' : access;
}

function fromApiItem(item: InventoryItem): InventoryRow {
    // Cleared prices come back as `null` (per backend "clear via PATCH
    // price: null" semantics). The current designs render the table cell
    // as a numeric input with no "no price" affordance, so coerce to 0
    // at the boundary. If/when we want a distinct "no price set" cell,
    // widen `InventoryRow.currentLabPrice` to `number | null` instead.
    //
    // `borrowStatus` and `quantityUsed` are typed on the wire today.
    // `dailyRevenue` is not yet in the swagger; the boundary cast picks
    // it up the moment it ships without further plumbing.
    const raw = item as unknown as Record<string, unknown>;
    const dailyRevenue = typeof raw.dailyRevenue === 'number' ? raw.dailyRevenue : null;
    return {
        id: item.id,
        symbol: item.symbol,
        currentQuantity: item.quantity,
        currentLabPrice: item.price ?? 0,
        borrowStatus: item.borrowStatus ?? null,
        quantityUsed: item.quantityUsed ?? null,
        dailyRevenue,
        currentPrice: item.currentPrice ?? null,
        fairValuePrice: item.fairValuePrice ?? null,
    };
}

/**
 * Order roles by privilege so the de-dup step in
 * {@link collectPoolPermissionHolders} keeps the most-privileged tier
 * an entity holds. The numeric values are arbitrary — only the
 * relative ordering matters.
 */
const ROLE_RANK: Record<PermissionRole, number> = {
    reader: 1,
    writer: 2,
    owner: 3,
    admin: 4,
};

/**
 * Extract the user UUIDs (and nothing else) from a single
 * `PermissionDirective` array. The Permissions tab is scoped to firm
 * members; group / firm / special / deny directives all return null
 * so they're filtered out at the top level.
 */
function userIdFromDirective(directive: PermissionDirective): string | null {
    if (isTypedPermissionDirective(directive)) {
        if (directive.mode !== 'allow') return null;
        return directive.type === 'user' ? directive.id : null;
    }
    if (typeof directive === 'string') {
        // Bare-string directives carry no type on the wire. We assume
        // any non-special UUID is a user — special pseudo-subjects
        // (`everyone`, etc.) are intentionally excluded.
        return isSpecialPermissionSubject(directive) ? null : directive;
    }
    return null;
}

/**
 * Walk a pool's `AppliedPermissions` and produce one row per unique
 * user that holds at least one allow directive. Group/firm/special
 * subjects are filtered out — the Permissions tab is firm-members-only.
 * Each row gets the user's highest rung; `userName` and `email` are
 * resolved through the firm-members directory, and users that don't
 * resolve are dropped from the list (UUIDs are never shown).
 */
function collectPoolPermissionHolders(
    pool: InventoryPool | undefined,
    memberLookup: ReadonlyMap<string, { firstName: string; lastName: string; email: string }>,
): ReadonlyArray<PoolPermissionHolder> {
    if (!pool) return [];
    const highestByUser = new Map<string, PermissionRole>();

    const consume = (directives: PermissionDirective[], role: PermissionRole) => {
        for (const directive of directives) {
            const userId = userIdFromDirective(directive);
            if (!userId) continue;
            const existing = highestByUser.get(userId);
            if (existing && ROLE_RANK[existing] >= ROLE_RANK[role]) continue;
            highestByUser.set(userId, role);
        }
    };
    consume(pool.permissions.admins, 'admin');
    consume(pool.permissions.owners, 'owner');
    consume(pool.permissions.writers, 'writer');
    consume(pool.permissions.readers, 'reader');

    const out: PoolPermissionHolder[] = [];
    for (const [userId, role] of highestByUser) {
        const profile = memberLookup.get(userId);
        if (!profile) continue;
        const userName = `${profile.firstName} ${profile.lastName}`.trim() || profile.email;
        out.push({ userId, userName, email: profile.email, role });
    }
    return out;
}

function fromApiPool(p: InventoryPool, statusOverride?: PoolStatus): Pool {
    return {
        id: p.id,
        name: p.name,
        description: p.description,
        firmId: p.firmId,
        permission: fromEffectiveAccess(p.permissions.effectiveAccess),
        // Backend exposes a boolean `active`; the UI surfaces a richer
        // `'active' | 'paused'` enum so future statuses (e.g. `draining`)
        // can land without re-keying templates. The override map only
        // wins while an optimistic toggle is in flight.
        status: statusOverride ?? (p.active ? 'active' : 'paused'),
    };
}

@injectable()
export class PoolManagementModelImpl implements PoolManagementModel {
    readonly #disposableStack = new DisposableStack();

    // --- Reducer-backed state ---
    readonly #state = signal<PoolManagementState>(initialPoolManagementState);

    /**
     * Firm scope. `undefined` = not yet initialized (queries disabled);
     * `null` = explicitly superuser scope (list-all); `string` = firm-scoped.
     */
    readonly #firmId = signal<string | null | undefined>(undefined);

    /**
     * `firmId → firmName` lookup, owned here so the search filter and
     * the detail view can read through it. `null` outside the superuser
     * view (where the firm-scoped page subtitle already names the firm).
     */
    readonly #firmNamesByFirmId = signal<Readonly<Record<string, string>> | null>(null);

    /**
     * Locally-added rows that have not yet been persisted. Keyed by
     * pool id so swapping pools doesn't lose the user's pending adds.
     * The row id starts with {@link PENDING_ROW_PREFIX} so the reducer
     * can distinguish "drop the pending add" from "stage backend removal".
     */
    readonly #pendingAddsByPool = signal<ReadonlyMap<string, ReadonlyArray<InventoryRow>>>(new Map());

    /**
     * Pool-status overrides, keyed by pool id. Pool status is derived
     * from `InventoryPool.active`; this map holds short-lived UI
     * overrides during an optimistic Pause/Resume PATCH so the pill
     * never flashes back to the pre-toggle value between the request
     * and the cache patch. On success the entry is removed so the UI
     * falls back to the canonical `InventoryPool.active` (which the
     * success path also patches into the React-Query cache); on
     * failure the entry rolls back to the previous override. Survives
     * pool-switch but not page reloads.
     */
    readonly #poolStatusOverrides = signal<ReadonlyMap<string, PoolStatus>>(new Map());

    /**
     * Per-pool monotonic counter. Each `#setPoolStatus` call bumps the
     * version for `poolId`; success and error handlers only apply their
     * effects when their captured version still matches. Without this,
     * a fast pause → resume → pause sequence whose API calls settle out
     * of order can roll the optimistic state back to a stale value.
     */
    readonly #poolStatusMutationVersion = new Map<string, number>();

    /**
     * Pool currently awaiting a pause-confirmation. Non-null while the
     * confirm modal is open; the wizard clears it on cancel/confirm.
     */
    readonly #pendingPauseId = signal<string | null>(null);

    /**
     * Wall-clock ms of the most recent point at which the open pool's
     * view became fresh: either a manual `refresh()` settled or a
     * successful mutation (submit, upload, pool status toggle,
     * permissions change) finished invalidating its queries. The
     * toolbar's refresh stamp reads through this so it ticks "just
     * now" automatically after every pool change.
     */
    readonly #lastRefreshAt = signal<number>(Date.now());

    /**
     * Stages a role change for the Permissions tab confirm dialog.
     * Carries the pool id captured at request time so the confirm
     * targets the original pool even if the user switches selection
     * before pressing Apply. Null when no confirm is pending.
     */
    readonly #pendingPermissionChange = signal<{
        poolId: string;
        userId: string;
        role: PermissionRole;
    } | null>(null);

    /**
     * In-flight permission mutations counter so the view can disable
     * controls while a PUT is settling. A counter (not a boolean)
     * because we may fan out multiple calls from a single user action
     * (e.g. revoke all tiers for a holder).
     */
    readonly #permissionsMutationsInFlight = signal<number>(0);

    /**
     * Discard-guard callbacks. `#pendingDiscard` holds the action that
     * runs when the user confirms; `#pendingDiscardCancel` (optional)
     * runs when they cancel. Callback-based rather than typed-intent so
     * the React shim's route blocker can park its `proceed` / `reset`
     * pair here without the model knowing about react-router.
     */
    readonly #pendingDiscard = signal<(() => void) | null>(null);
    readonly #pendingDiscardCancel = signal<(() => void) | null>(null);

    // --- Query observers ---
    readonly #poolsQuery: QueryObserverModel<ListPoolsResponse, Error, ListPoolsResponse>;
    readonly #itemsQuery: QueryObserverModel<ListPoolItemsResponse, Error, ListPoolItemsResponse>;
    readonly #changeLogQuery: QueryObserverModel<GetPoolChangeLogResponse, Error, GetPoolChangeLogResponse>;
    /**
     * Firm members of the selected pool's firm — only fetched once the
     * Permissions tab is active, to keep the directory request lazy.
     * The Permissions tab uses this to resolve user UUIDs to names and
     * to populate the grant-access selector.
     */
    readonly #firmMembersQuery: QueryObserverModel<
        ReadonlyArray<FirmMembershipWithProfile>,
        Error,
        ReadonlyArray<FirmMembershipWithProfile>
    >;
    readonly #queryClient: QueryClient;
    readonly #store: ReactBindings['Store'];
    readonly #currentUser: ReactBindings['CurrentUserModel'];

    constructor(
        @inject('QueryObserverModelFactory')
        queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('QueryClient') queryClient: QueryClient,
        @inject('Store') store: ReactBindings['Store'],
        @inject('CurrentUserModel') currentUser: ReactBindings['CurrentUserModel'],
    ) {
        this.#queryClient = queryClient;
        this.#store = store;
        this.#currentUser = currentUser;
        const poolsOptions = computed<QueryObserverOptions<ListPoolsResponse, Error, ListPoolsResponse>>(() => {
            const firmId = this.#firmId.value;
            // `null` is the explicit "superuser, list all" mode; `undefined`
            // means the page hasn't told us which firm yet, so disable the
            // firm-scoped query until it does.
            if (firmId === null) return getAllPoolsQuery();
            return getFirmPoolsQuery(firmId);
        });
        this.#poolsQuery = queryObserverModelFactory<ListPoolsResponse, Error, ListPoolsResponse>(poolsOptions);
        this.#disposableStack.use(this.#poolsQuery);

        const itemsOptions = computed<QueryObserverOptions<ListPoolItemsResponse, Error, ListPoolItemsResponse>>(() => {
            const s = this.#state.value;
            const poolId = s.selectedPoolId ?? undefined;
            // A symbol search is answered by the backend: pass the
            // committed term as the `symbol` parameter. The endpoint
            // returns every matching item and ignores skip/limit, so we
            // don't page it here.
            const search = s.appliedSymbolSearch.trim();
            if (search !== '') {
                return getPoolItemsQuery(poolId, { symbol: search });
            }
            // "All" rows-per-page loads the full pool (the Daily Total
            // summary needs every row), paging the backend for real
            // rather than requesting one oversized limit. Otherwise page
            // server-side with the current page / page-size.
            if (s.inventoryRowsPerPage === 'all') {
                return getAllPoolItemsQuery(poolId);
            }
            const perPage = s.inventoryRowsPerPage;
            // Guard against NaN / Infinity / fractional page state — the
            // reducer doesn't constrain `SET_INVENTORY_PAGE` callers, so
            // a bad caller could otherwise propagate a garbage `skip`
            // into the request. Display-side clamping against
            // `inventoryPageCount` would close the loop on stale rows
            // when total shrinks, but reading the count here creates a
            // signal cycle (the count comes from this very query).
            const requested = s.inventoryPage;
            const page = Number.isFinite(requested) ? Math.max(1, Math.trunc(requested)) : 1;
            return getPoolItemsQuery(poolId, { skip: (page - 1) * perPage, limit: perPage });
        });
        this.#itemsQuery = queryObserverModelFactory<ListPoolItemsResponse, Error, ListPoolItemsResponse>(itemsOptions);
        this.#disposableStack.use(this.#itemsQuery);

        const changeLogOptions = computed<
            QueryObserverOptions<GetPoolChangeLogResponse, Error, GetPoolChangeLogResponse>
        >(() => {
            const s = this.#state.value;
            // Only fetch when the user is actually viewing the change-log
            // tab — the audit log is large enough that we don't want to
            // refresh it eagerly alongside the inventory grid.
            if (s.activePoolDetailTab !== 'change-log') {
                return getPoolChangeLogQuery(undefined);
            }
            return getPoolChangeLogQuery(s.selectedPoolId ?? undefined, { skip: 0, limit: CHANGE_LOG_PAGE_SIZE });
        });
        this.#changeLogQuery = queryObserverModelFactory<GetPoolChangeLogResponse, Error, GetPoolChangeLogResponse>(
            changeLogOptions,
        );
        this.#disposableStack.use(this.#changeLogQuery);

        const firmMembersOptions = computed<
            QueryObserverOptions<
                ReadonlyArray<FirmMembershipWithProfile>,
                Error,
                ReadonlyArray<FirmMembershipWithProfile>
            >
        >(() => {
            const s = this.#state.value;
            // The members directory is only needed by the Permissions tab.
            // Disable the query when the tab isn't open; the firm-id key
            // means the cache is shared with any other admin surface that
            // already fetched it.
            const pool = s.selectedPoolId
                ? this.#poolsQuery.signal.value.data?.pools.find((p) => p.id === s.selectedPoolId)
                : undefined;
            // Only fire the firm-members query when the Permissions tab
            // is actually accessible: stored tab is `permissions` AND the
            // caller has superuser access. Non-superusers store
            // `permissions` as their default but can't see the tab, so
            // the directory fetch would otherwise be wasted.
            const showsPermissions = s.activePoolDetailTab === 'permissions' && this.canManagePermissions.value;
            const firmId = showsPermissions ? pool?.firmId : undefined;
            return getFirmMembersQuery(firmId);
        });
        this.#firmMembersQuery = queryObserverModelFactory<
            ReadonlyArray<FirmMembershipWithProfile>,
            Error,
            ReadonlyArray<FirmMembershipWithProfile>
        >(firmMembersOptions);
        this.#disposableStack.use(this.#firmMembersQuery);

        // When `canManagePermissions` flips off (superuser session
        // expires or is toggled off externally) any in-flight permission-
        // change confirmation must be dropped — the dialog would
        // otherwise sit open with a callback that re-enters
        // `#putUserPermission` even though the tab is now hidden.
        const disposeSuperuserWatcher = effect(() => {
            if (!this.canManagePermissions.value) {
                this.#pendingPermissionChange.value = null;
            }
        });
        this.#disposableStack.defer(disposeSuperuserWatcher);
        // Drop any pending debounced symbol search so it can't dispatch
        // into a disposed model.
        this.#disposableStack.defer(() => this.#applySymbolSearch.cancel());
    }

    #dispatch(action: PoolManagementAction): void {
        this.#state.value = poolManagementReducer(this.#state.value, action);
    }

    /**
     * Enqueue a persistent "in-flight" toast and return its id so the caller
     * can dismiss it once the action settles. Callers should follow up with
     * {@link #replaceToast} on both success and failure so the loader never
     * lingers after the work is done.
     */
    #startActionToast(message: string): string {
        const id = crypto.randomUUID();
        this.#store.dispatch({
            type: 'toast::enqueue',
            toast: {
                id,
                message,
                group: 'default',
                variant: 'info',
                autoHideDuration: null,
            },
        });
        return id;
    }

    #replaceToast(startId: string, finalToast: { message: string; variant: 'success' | 'error' }): void {
        this.#store.dispatch({ type: 'toast::dismiss', id: startId });
        this.#store.dispatch({
            type: 'toast::enqueue',
            toast: {
                message: finalToast.message,
                group: 'default',
                variant: finalToast.variant,
            },
        });
    }

    /**
     * Sets the firm scope for this model. Pass `null` for the superuser
     * "list all pools" mode. Resets the open pool and any in-flight
     * edits when the scope changes. (Firm display name is owned by the
     * page, not the model — see `PoolManagementPage`.)
     */
    setFirm(firmId: string | null): void {
        if (this.#firmId.value === firmId) return;
        // Unsaved edits in the current pool? Park the rest behind the
        // discard prompt; the closure below runs verbatim after confirm.
        this.#runOrPromptDiscard(() => this.#doSetFirm(firmId));
    }

    #doSetFirm(firmId: string | null): void {
        // Drop any in-flight permission-change confirmation: the captured
        // `poolId` is stale once scope changes, and reopening another pool
        // would otherwise remount the dialog and mutate the old pool.
        this.#pendingPermissionChange.value = null;
        this.#firmId.value = firmId;
        // Drop the superuser-only firm-name map when leaving superuser
        // scope so `filteredPools` and `selectedPoolFirmName` can rely on
        // "map non-null ↔ superuser scope" without trusting the caller to
        // also dispatch `setFirmNames(null)`.
        if (firmId !== null) this.#firmNamesByFirmId.value = null;
        this.#dispatch(openPoolList());
        this.#pendingAddsByPool.value = new Map();
        // CLOSE_UPLOAD_WIZARD owns the upload-lifecycle teardown
        // (wizard, applying, error, summary) in a single reducer
        // transition — see its reducer case. OPEN_POOL_LIST above
        // already cleared the submit lifecycle (isSubmitting,
        // submitError) along with the per-pool edit slate.
        this.#dispatch(closeUploadWizard());
    }

    setFirmNames(map: Readonly<Record<string, string>> | null): void {
        this.#firmNamesByFirmId.value = map;
    }

    // --- Loading / error signals ---
    readonly isLoading: ReadonlySignal<boolean> = computed(() => {
        if (this.#poolsQuery.signal.value.isLoading) return true;
        if (this.#state.value.selectedPoolId && this.#itemsQuery.signal.value.isLoading) return true;
        return false;
    });
    readonly error: ReadonlySignal<string | null> = computed(() => {
        const e = this.#poolsQuery.signal.value.error ?? this.#itemsQuery.signal.value.error;
        return e ? e.message : null;
    });

    // --- Pool list signals ---
    readonly pools: ReadonlySignal<ReadonlyArray<Pool>> = computed(() => {
        const overrides = this.#poolStatusOverrides.value;
        return this.#poolsQuery.signal.value.data?.pools.map((p) => fromApiPool(p, overrides.get(p.id))) ?? [];
    });
    readonly poolSearchQuery: ReadonlySignal<string> = computed(() => this.#state.value.poolSearchQuery);
    readonly firmNamesByFirmId: ReadonlySignal<Readonly<Record<string, string>> | null> = computed(
        () => this.#firmNamesByFirmId.value,
    );
    readonly filteredPools: ReadonlySignal<ReadonlyArray<Pool>> = computed(() => {
        const query = this.#state.value.poolSearchQuery.trim().toLowerCase();
        const all = this.pools.value;
        if (!query) return all;
        const firmNames = this.#firmNamesByFirmId.value;
        return all.filter((p) => {
            if (p.name.toLowerCase().includes(query)) return true;
            if ((p.description ?? '').toLowerCase().includes(query)) return true;
            // Firm-name matching is superuser-only: the map is null in the
            // firm-scoped view (where every pool shares the same firm).
            if (firmNames !== null) {
                const firmName = firmNames[p.firmId];
                if (firmName && firmName.toLowerCase().includes(query)) return true;
            }
            return false;
        });
    });

    // --- View / selection signals ---
    readonly view: ReadonlySignal<PoolManagementView> = computed(() => this.#state.value.view);
    readonly selectedPoolId: ReadonlySignal<string | null> = computed(() => this.#state.value.selectedPoolId);
    readonly selectedPool: ReadonlySignal<Pool | null> = computed(() => {
        const id = this.#state.value.selectedPoolId;
        if (!id) return null;
        return this.pools.value.find((p) => p.id === id) ?? null;
    });
    readonly selectedPoolPermission: ReadonlySignal<PoolPermission | null> = computed(
        () => this.selectedPool.value?.permission ?? null,
    );
    readonly selectedPoolFirmName: ReadonlySignal<string | null> = computed(() => {
        const pool = this.selectedPool.value;
        if (!pool) return null;
        const map = this.#firmNamesByFirmId.value;
        if (!map) return null;
        return map[pool.firmId] ?? null;
    });

    // --- Permission gates ---
    readonly canRead: ReadonlySignal<boolean> = computed(() => this.selectedPoolPermission.value !== null);
    readonly canWrite: ReadonlySignal<boolean> = computed(() => {
        const p = this.selectedPoolPermission.value;
        return p === 'writer' || p === 'owner';
    });
    readonly canOwn: ReadonlySignal<boolean> = computed(() => this.selectedPoolPermission.value === 'owner');

    // --- Inventory row signals ---
    /**
     * Server-side rows plus any locally pending adds for the current pool.
     * Locally pending rows carry the {@link PENDING_ROW_PREFIX} so the
     * remove path can distinguish "drop the pending add" from "stage
     * backend removal".
     */
    readonly serverInventoryRows: ReadonlySignal<ReadonlyArray<InventoryRow>> = computed(() => {
        const id = this.#state.value.selectedPoolId;
        if (!id) return [];
        const remote = this.#itemsQuery.signal.value.data?.items.map(fromApiItem) ?? [];
        const pending = this.#pendingAddsByPool.value.get(id) ?? [];
        if (pending.length === 0) return remote;
        // Pending adds sit ABOVE server rows so a freshly-added symbol
        // is visible at the top of the list without scrolling. Within
        // the pending block, `#addSymbolInternal` keeps newest-first.
        return [...pending, ...remote];
    });

    readonly rowEdits: ReadonlySignal<Readonly<Record<string, InventoryRowEdit>>> = computed(
        () => this.#state.value.rowEdits,
    );

    /**
     * Memoised on the reducer's `pendingRowRemovals` array reference so
     * unrelated state changes (e.g. search query updates) don't churn a
     * fresh `Set` and propagate a "changed" event to every subscriber.
     */
    readonly pendingRowRemovals: ReadonlySignal<ReadonlySet<string>> = (() => {
        let lastSource: ReadonlyArray<string> | null = null;
        let lastSet: ReadonlySet<string> = new Set();
        return computed(() => {
            const source = this.#state.value.pendingRowRemovals;
            if (source !== lastSource) {
                lastSource = source;
                lastSet = new Set(source);
            }
            return lastSet;
        });
    })();

    readonly displayedInventoryRows: ReadonlySignal<ReadonlyArray<InventoryRow>> = computed(() => {
        const rows = this.serverInventoryRows.value;
        const edits = this.#state.value.rowEdits;
        if (Object.keys(edits).length === 0) return rows;
        return rows.map((row) => {
            const edit = edits[row.id];
            if (!edit) return row;
            return {
                ...row,
                currentQuantity: edit.currentQuantity ?? row.currentQuantity,
                currentLabPrice: edit.currentLabPrice ?? row.currentLabPrice,
            };
        });
    });

    /**
     * Pending draft rows for the selected pool with field-edits
     * applied. The Permissions / inventory table renders these in a
     * separate section above the paginated server rows so they stay
     * visible regardless of the current page, filter, or page size.
     * They never appear in {@link paginatedInventoryRows} or
     * {@link filteredInventoryRows} and they don't count toward
     * {@link inventoryTotalCount}; callers that need the union
     * (submit review, CSV download) use {@link displayedInventoryRows}.
     */
    readonly pendingDraftRows: ReadonlySignal<ReadonlyArray<InventoryRow>> = computed(() => {
        const id = this.#state.value.selectedPoolId;
        if (!id) return [];
        const pending = this.#pendingAddsByPool.value.get(id);
        if (!pending || pending.length === 0) return [];
        const edits = this.#state.value.rowEdits;
        if (Object.keys(edits).length === 0) return pending;
        return pending.map((row) => {
            const edit = edits[row.id];
            if (!edit) return row;
            return {
                ...row,
                currentQuantity: edit.currentQuantity ?? row.currentQuantity,
                currentLabPrice: edit.currentLabPrice ?? row.currentLabPrice,
            };
        });
    });

    readonly filteredInventoryRows: ReadonlySignal<ReadonlyArray<InventoryRow>> = computed(() => {
        // Server rows for the current view, excluding drafts (which
        // render in their own always-visible section above the table).
        // When a symbol search is active the backend has already
        // narrowed these to the matching items, so there is no
        // client-side filtering left to do here.
        return this.displayedInventoryRows.value.filter((r) => !isDraftRowId(r.id));
    });

    readonly totalDailyRevenue: ReadonlySignal<number | null> = computed(() => {
        const rows = this.serverInventoryRows.value;
        let sum = 0;
        let any = false;
        for (const r of rows) {
            if (r.dailyRevenue !== null) {
                sum += r.dailyRevenue;
                any = true;
            }
        }
        // Return null when no row exposed a value so the toolbar can
        // render an em-dash placeholder instead of "$0".
        return any ? sum : null;
    });

    // --- Pagination ---
    readonly inventoryRowsPerPage: ReadonlySignal<InventoryRowsPerPage> = computed(
        () => this.#state.value.inventoryRowsPerPage,
    );
    /**
     * Total number of server-side items in the pool. Backed by the
     * `count` field added in locates-ws #449. Pending draft adds are
     * deliberately excluded — they render in their own always-visible
     * section above the paginated table and don't participate in the
     * "showing X-Y of N" label. Returns `0` while the query hasn't
     * reported a count yet.
     */
    readonly inventoryTotalCount: ReadonlySignal<number> = computed(() => {
        const id = this.#state.value.selectedPoolId;
        if (!id) return 0;
        return this.#itemsQuery.signal.value.data?.count ?? 0;
    });
    readonly inventoryPageCount: ReadonlySignal<number> = computed(() => {
        const perPage = this.#state.value.inventoryRowsPerPage;
        if (perPage === 'all') return 1;
        // `inventoryTotalCount` is the right total in both modes: a paged
        // fetch reports the full pool size, and a symbol search reports
        // the match count (the backend sets `count === items.length`).
        return Math.max(1, Math.ceil(this.inventoryTotalCount.value / perPage));
    });
    /**
     * Clamped page index. The stored `inventoryPage` is user-driven and
     * could be left out-of-range after the filter narrows; this view
     * snaps it back into `[1, pageCount]` so consumers don't have to.
     */
    readonly inventoryPage: ReadonlySignal<number> = computed(() => {
        const requested = this.#state.value.inventoryPage;
        const max = this.inventoryPageCount.value;
        if (!Number.isFinite(requested) || requested < 1) return 1;
        if (requested > max) return max;
        return Math.floor(requested);
    });
    readonly paginatedInventoryRows: ReadonlySignal<ReadonlyArray<InventoryRow>> = computed(() => {
        const s = this.#state.value;
        const filtering = s.appliedSymbolSearch.trim() !== '';
        const perPage = s.inventoryRowsPerPage;
        // Filter-active and "all" both bypass server paging (the symbol
        // search returns every match), so the slice is purely client-side.
        // Drafts are excluded here — they render in their own section
        // above the table and never participate in filter / page math.
        if (filtering || perPage === 'all') {
            const rows = this.filteredInventoryRows.value;
            if (perPage === 'all' || !filtering) return rows;
            // Filtering with a numeric page size: still slice client-side
            // so the user can page through search results.
            const page = this.inventoryPage.value;
            const start = (page - 1) * perPage;
            return rows.slice(start, start + perPage);
        }
        // Paged server fetch: the query already holds the right page;
        // we just need to strip any draft rows that snuck in via
        // `displayedInventoryRows` (drafts render in their own section
        // above the table — see {@link pendingDraftRows}).
        return this.displayedInventoryRows.value.filter((r) => !isDraftRowId(r.id));
    });
    setInventoryPage(page: number): void {
        this.#dispatch(setInventoryPage(page));
    }
    setInventoryRowsPerPage(rowsPerPage: InventoryRowsPerPage): void {
        this.#dispatch(setInventoryRowsPerPage(rowsPerPage));
    }

    // --- Detail-view editing signals ---
    readonly symbolSearchQuery: ReadonlySignal<string> = computed(() => this.#state.value.symbolSearchQuery);
    readonly addSymbolDraft: ReadonlySignal<string> = computed(() => this.#state.value.addSymbolDraft);
    readonly isDirty: ReadonlySignal<boolean> = computed(() => {
        const s = this.#state.value;
        if (Object.keys(s.rowEdits).length > 0) return true;
        if (s.pendingRowRemovals.length > 0) return true;
        // A typed-but-not-yet-added symbol counts as unsaved work: any
        // navigation/reset guard that consults `isDirty` should prompt the
        // user before discarding it.
        if (s.addSymbolDraft.trim() !== '') return true;
        const poolId = s.selectedPoolId;
        if (poolId && (this.#pendingAddsByPool.value.get(poolId)?.length ?? 0) > 0) return true;
        return false;
    });
    readonly unsavedChangeCount: ReadonlySignal<number> = computed(() => {
        const s = this.#state.value;
        const poolId = s.selectedPoolId;
        const additions = poolId ? (this.#pendingAddsByPool.value.get(poolId)?.length ?? 0) : 0;
        const removals = new Set(s.pendingRowRemovals);
        // Edits on draft rows are part of constructing the addition; edits
        // on rows already staged for removal will be discarded on submit.
        const edits = Object.keys(s.rowEdits).filter((id) => !isDraftRowId(id) && !removals.has(id)).length;
        return additions + edits + removals.size;
    });
    readonly submitReviewOpen: ReadonlySignal<boolean> = computed(() => this.#state.value.submitReviewOpen);

    // --- Submit lifecycle (driven entirely by reducer state) ---
    readonly isSubmitting: ReadonlySignal<boolean> = computed(() => this.#state.value.isSubmitting);
    readonly submitError: ReadonlySignal<string | null> = computed(() => this.#state.value.submitError);

    // --- Navigation commands ---
    openPoolList(): void {
        this.#runOrPromptDiscard(() => {
            this.#pendingPermissionChange.value = null;
            this.#dispatch(openPoolList());
        });
    }
    openPoolDetail(poolId: string): void {
        // A click on the already-open pool would otherwise burn an
        // unnecessary discard prompt; short-circuit when the target is
        // the current pool.
        if (this.#state.value.selectedPoolId === poolId && this.#state.value.view === 'detail') return;
        this.#runOrPromptDiscard(() => {
            this.#pendingPermissionChange.value = null;
            this.#dispatch(openPoolDetail(poolId));
            // The pool-detail query observers refetch when `poolId`
            // changes, so the view is fresh as of right now — reset
            // the stamp so the toolbar doesn't read "Last refresh: 5
            // minutes ago" against data we just loaded.
            this.#markFresh();
        });
    }

    // --- Unsaved-changes guard ---
    readonly discardChangesOpen: ReadonlySignal<boolean> = computed(() => this.#pendingDiscard.value !== null);

    /**
     * Run `proceed` immediately when there are no unsaved edits.
     * Otherwise stash it behind the discard prompt: `confirmDiscardChanges`
     * runs it after wiping edits, `cancelDiscardChanges` runs `onCancel`
     * (if provided) so the caller can react to the user keeping their
     * work. Used internally by the navigation methods and externally by
     * the React shim's route blocker.
     */
    requestDiscardChanges(proceed: () => void, onCancel?: () => void): void {
        this.#runOrPromptDiscard(proceed, onCancel);
    }

    #runOrPromptDiscard(proceed: () => void, onCancel?: () => void): void {
        if (this.isDirty.value) {
            // Re-entrancy: a second navigation request can arrive while
            // a prompt is already open (e.g. the router blocker fires
            // again on a popstate). Fire the existing `onCancel` first
            // so any external blocker waiting on it (e.g. `blocker.reset`)
            // gets resolved before its callback is replaced.
            const previousCancel = this.#pendingDiscardCancel.value;
            this.#pendingDiscard.value = proceed;
            this.#pendingDiscardCancel.value = onCancel ?? null;
            previousCancel?.();
            return;
        }
        proceed();
    }

    confirmDiscardChanges(): void {
        const proceed = this.#pendingDiscard.value;
        if (!proceed) return;
        this.#pendingDiscard.value = null;
        // Confirm wins over cancel — drop the cancel callback so a
        // later `cancelDiscardChanges` (e.g. a delayed `blocker.reset`)
        // can't double-fire it.
        this.#pendingDiscardCancel.value = null;
        // Capture the current pool id BEFORE invoking `proceed`: the
        // callback may navigate to another pool (or back to the list),
        // which would change `selectedPoolId` before we clear the
        // pending-adds map. Drop pending adds for the pool we're
        // leaving. The reducer's OPEN_POOL_LIST / OPEN_POOL_DETAIL
        // transitions clear rowEdits, pendingRowRemovals,
        // addSymbolDraft, and submit state already; firm switches handle
        // pendingAdds inside `#doSetFirm`.
        const currentPoolId = this.#state.value.selectedPoolId;
        if (currentPoolId) {
            const next = new Map(this.#pendingAddsByPool.value);
            if (next.delete(currentPoolId)) this.#pendingAddsByPool.value = next;
        }
        proceed();
    }

    cancelDiscardChanges(): void {
        if (this.#pendingDiscard.value === null) return;
        const onCancel = this.#pendingDiscardCancel.value;
        this.#pendingDiscard.value = null;
        this.#pendingDiscardCancel.value = null;
        onCancel?.();
    }

    // --- Pool detail tabs ---
    /**
     * Session-level access gate for the Permissions tab and its
     * mutations. Derived directly from `CurrentUserModel` so the model
     * picks up superuser toggles automatically without a separate
     * React-side `setSuperuserEnabled` round-trip. `isSuperuser` is the
     * static "this user is allowed to be a superuser" flag;
     * `isSuperUserModeEnabled` is the time-bounded session toggle the
     * admin saga maintains (it clears when the access window expires).
     */
    readonly canManagePermissions: ReadonlySignal<boolean> = computed(
        () => this.#currentUser.isSuperuser.value && this.#currentUser.isSuperUserModeEnabled.value,
    );

    readonly lastRefreshAt: ReadonlySignal<number> = computed(() => this.#lastRefreshAt.value);

    /**
     * Bump the refresh stamp to `now()`. Called from every successful
     * mutation path after its query invalidations settle, so the
     * toolbar's "Last refresh" label always reflects the moment the
     * pool view actually became fresh (manual refresh, submit, upload,
     * pool status toggle, permissions update).
     */
    #markFresh(): void {
        this.#lastRefreshAt.value = Date.now();
    }

    readonly activePoolDetailTab: ReadonlySignal<PoolDetailTab> = computed(() => {
        const stored = this.#state.value.activePoolDetailTab;
        // Permissions is the stored default, but non-superusers can't
        // see the tab — coerce on read so they always land on `lab-pool`.
        if (stored === 'permissions' && !this.canManagePermissions.value) return 'lab-pool';
        return stored;
    });
    setActivePoolDetailTab(tab: PoolDetailTab): void {
        // Hard-stop attempts to land on Permissions when the user can't
        // see it; this prevents the view from briefly opening the tab on
        // a stale dispatch.
        if (tab === 'permissions' && !this.canManagePermissions.value) return;
        this.#dispatch(setActivePoolDetailTab(tab));
    }

    // --- Change Log tab ---
    /**
     * First page of the per-pool audit trail, sourced from
     * `GET /v1/inventory/pools/{id}/log`. Filter signals (symbol,
     * action bucket, time range) narrow the list client-side because
     * the backend endpoint takes only `skip`/`limit` today. Returns
     * an empty array while the query is loading or disabled.
     */
    /**
     * `true` while the change-log query for the open pool is fetching
     * its first page (no cached data yet). Lets the view show a loading
     * placeholder instead of the empty-state CTA during the gap between
     * activating the tab and the response arriving.
     */
    readonly changeLogLoading: ReadonlySignal<boolean> = computed(() => {
        const q = this.#changeLogQuery.signal.value;
        return q.data === undefined && q.isLoading === true;
    });

    readonly changeLogEntries: ReadonlySignal<ReadonlyArray<InventoryChangeEntry>> = computed(() => {
        const data = this.#changeLogQuery.signal.value.data;
        if (!data) return [];
        // Newest-first; backend ordering isn't part of the contract so we
        // sort here to keep the timeline / day grouping stable.
        const mapped = data.entries.map(fromApiChangeLogEntry).sort((a, b) => b.timestamp - a.timestamp);
        const s = this.#state.value;
        const symbolQuery = s.changeLogSymbolFilter.trim().toLowerCase();
        const actionFilter = s.changeLogActionFilter;
        const timeRange = s.changeLogTimeRange;
        const cutoff = timeRangeCutoffMs(timeRange);
        return mapped.filter((entry) => {
            if (symbolQuery && !entry.symbol.toLowerCase().includes(symbolQuery)) return false;
            // Sign-based filter: the journal `action` code can't tell a
            // delete apart from a quantity reduction, so we use the
            // quantity sign as ground truth. Zero-quantity entries
            // (price-only updates) match neither sign-specific filter.
            if (actionFilter === 'increase' && !(entry.quantity > 0)) return false;
            if (actionFilter === 'decrease' && !(entry.quantity < 0)) return false;
            if (cutoff !== null && entry.timestamp < cutoff) return false;
            return true;
        });
    });
    readonly changeLogSymbolFilter: ReadonlySignal<string> = computed(() => this.#state.value.changeLogSymbolFilter);
    readonly changeLogActionFilter: ReadonlySignal<ChangeLogActionFilter> = computed(
        () => this.#state.value.changeLogActionFilter,
    );
    readonly changeLogTimeRange: ReadonlySignal<ChangeLogTimeRange> = computed(
        () => this.#state.value.changeLogTimeRange,
    );
    setChangeLogSymbolFilter(query: string): void {
        this.#dispatch(setChangeLogSymbolFilter(query));
    }
    setChangeLogActionFilter(filter: ChangeLogActionFilter): void {
        this.#dispatch(setChangeLogActionFilter(filter));
    }
    setChangeLogTimeRange(range: ChangeLogTimeRange): void {
        this.#dispatch(setChangeLogTimeRange(range));
    }

    // --- Pool status (Pause / Resume) ---
    readonly pendingPauseId: ReadonlySignal<string | null> = computed(() => this.#pendingPauseId.value);

    /**
     * Open the pause-confirmation modal for a pool. No-ops if the pool
     * is already paused or the caller can't write.
     */
    requestPausePool(poolId: string): void {
        const pool = this.pools.value.find((p) => p.id === poolId);
        if (!pool || pool.status !== 'active') return;
        if (pool.permission === 'reader') return;
        this.#pendingPauseId.value = poolId;
    }
    cancelPausePool(): void {
        this.#pendingPauseId.value = null;
    }
    /**
     * Commit the pending pause (called from the confirm modal). Drops
     * the pending id whether or not the toggle actually fires; the toast
     * surfaces the resulting state for visibility.
     */
    confirmPausePool(): void {
        const poolId = this.#pendingPauseId.value;
        this.#pendingPauseId.value = null;
        if (!poolId) return;
        this.#setPoolStatus(poolId, 'paused');
    }
    /**
     * Flip a paused pool back to active. No confirmation prompt — the
     * action is recoverable by pausing again.
     */
    resumePool(poolId: string): void {
        const pool = this.pools.value.find((p) => p.id === poolId);
        if (!pool || pool.status !== 'paused') return;
        if (pool.permission === 'reader') return;
        this.#setPoolStatus(poolId, 'active');
    }

    // --- Pool permissions ---
    /**
     * `userId -> { firstName, lastName, email }` derived from the
     * firm-members query. Used to resolve the holder list and to
     * power the grant-access selector.
     */
    readonly #memberLookup: ReadonlySignal<
        ReadonlyMap<string, { firstName: string; lastName: string; email: string }>
    > = computed(() => {
        const members = this.#firmMembersQuery.signal.value.data ?? [];
        const map = new Map<string, { firstName: string; lastName: string; email: string }>();
        for (const m of members) {
            const profile = m as unknown as {
                userId?: string;
                profile?: { firstName: string; lastName: string; email: string };
            };
            const userId = profile.userId;
            if (!userId || !profile.profile) continue;
            map.set(userId, profile.profile);
        }
        return map;
    });

    readonly poolPermissionHolders: ReadonlySignal<ReadonlyArray<PoolPermissionHolder>> = computed(() => {
        const id = this.#state.value.selectedPoolId;
        if (!id) return [];
        const raw = this.#poolsQuery.signal.value.data?.pools.find((p) => p.id === id);
        return collectPoolPermissionHolders(raw, this.#memberLookup.value);
    });

    readonly poolPermissionCandidates: ReadonlySignal<ReadonlyArray<PoolPermissionCandidate>> = computed(() => {
        const holders = this.poolPermissionHolders.value;
        const taken = new Set(holders.map((h) => h.userId));
        const out: PoolPermissionCandidate[] = [];
        for (const [userId, profile] of this.#memberLookup.value) {
            if (taken.has(userId)) continue;
            const userName = `${profile.firstName} ${profile.lastName}`.trim() || profile.email;
            out.push({ userId, userName, email: profile.email });
        }
        // Stable alphabetical sort so the dropdown order doesn't churn
        // when the underlying map iterates differently after a refetch.
        out.sort((a, b) => a.userName.localeCompare(b.userName));
        return out;
    });

    readonly pendingPermissionChange: ReadonlySignal<{
        userId: string;
        userName: string;
        role: PermissionRole;
    } | null> = computed(() => {
        const change = this.#pendingPermissionChange.value;
        if (!change) return null;
        const profile = this.#memberLookup.value.get(change.userId);
        const userName = profile ? `${profile.firstName} ${profile.lastName}`.trim() || profile.email : change.userId;
        return { userId: change.userId, userName, role: change.role };
    });

    readonly permissionsMutating: ReadonlySignal<boolean> = computed(
        () => this.#permissionsMutationsInFlight.value > 0,
    );

    requestSetPoolPermissionRole(userId: string, role: PermissionRole): void {
        if (!this.canManagePermissions.value || !this.canOwn.value) return;
        const pool = this.selectedPool.value;
        if (!pool) return;
        this.#pendingPermissionChange.value = { poolId: pool.id, userId, role };
    }

    cancelSetPoolPermissionRole(): void {
        this.#pendingPermissionChange.value = null;
    }

    async confirmSetPoolPermissionRole(): Promise<void> {
        const change = this.#pendingPermissionChange.value;
        if (!change) return;
        // Fail closed if either gate has been revoked since the dialog
        // opened: drop the pending change instead of firing a PATCH the
        // caller is no longer allowed to make. The owner check targets
        // the *captured* pool (in case the user has switched away)
        // rather than `selectedPool`.
        const pool = this.pools.value.find((p) => p.id === change.poolId);
        if (!this.canManagePermissions.value || pool?.permission !== 'owner') {
            this.#pendingPermissionChange.value = null;
            return;
        }
        this.#pendingPermissionChange.value = null;
        await this.#putUserPermission(change.poolId, change.userId, [{ access: change.role, kind: 'allow' }]);
    }

    async addPoolPermission(userId: string, role: PermissionRole): Promise<void> {
        if (!this.canManagePermissions.value || !this.canOwn.value) return;
        const pool = this.selectedPool.value;
        if (!pool) return;
        await this.#putUserPermission(pool.id, userId, [{ access: role, kind: 'allow' }]);
    }

    async removePoolPermission(userId: string): Promise<void> {
        if (!this.canManagePermissions.value || !this.canOwn.value) return;
        const pool = this.selectedPool.value;
        if (!pool) return;
        // Empty entries array clears every rung the user holds.
        await this.#putUserPermission(pool.id, userId, []);
    }

    /**
     * Issue the PUT against the `user` entity-type endpoint, increment /
     * decrement the in-flight counter so the view can disable controls,
     * dispatch success / error toasts, and invalidate the pool list so
     * the table refreshes with the server's new `permissions` payload.
     * `poolId` is captured by the caller, not read at fire time, so a
     * pool-switch between request and confirm doesn't redirect the PUT.
     */
    async #putUserPermission(poolId: string, userId: string, entries: PermissionUpdateEntry[]): Promise<void> {
        const profile = this.#memberLookup.value.get(userId);
        const displayName = profile ? `${profile.firstName} ${profile.lastName}`.trim() || profile.email : userId;
        this.#permissionsMutationsInFlight.value = this.#permissionsMutationsInFlight.value + 1;
        try {
            await updatePoolPermissionsForEntity({
                poolId,
                entityType: 'user',
                entityId: userId,
                entries,
            });
            await invalidatePoolsQuery(this.#queryClient, this.#firmId.value ?? undefined);
            this.#markFresh();
            const messageKey =
                entries.length === 0
                    ? 'admin::pool-management::toast-permissions-removed'
                    : 'admin::pool-management::toast-permissions-updated';
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: t(messageKey, { name: displayName }),
                    group: 'default',
                    variant: 'success',
                },
            });
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: t('admin::pool-management::toast-permissions-error', {
                        name: displayName,
                        message,
                    }),
                    group: 'default',
                    variant: 'error',
                },
            });
        } finally {
            this.#permissionsMutationsInFlight.value = this.#permissionsMutationsInFlight.value - 1;
        }
    }

    /**
     * Optimistically flip a pool's status, then call the backend. The
     * UI updates immediately so the pill / toast feel snappy; on
     * rejection we roll back the override and surface an error toast.
     *
     * Hits `PATCH /v1/inventory/pools/{poolId}` with `{ active }`. On
     * success we patch the pool-list cache with the response, then
     * clear the override entry entirely so the cache becomes the sole
     * source of truth — keeping a stale override would mask a later
     * server-side change (e.g. another admin pausing the pool). On
     * failure the override snaps back to whatever was there before.
     */
    #setPoolStatus(poolId: string, status: PoolStatus): void {
        const pool = this.pools.value.find((p) => p.id === poolId);
        if (!pool) return;
        // Centralized write-permission gate: callers can reach this path
        // after the pool's permission has dropped (the pause-confirm
        // modal can stay open while the user's access is revoked); fail
        // closed locally rather than relying on the server PATCH to
        // bounce.
        if (pool.permission === 'reader') return;
        if (pool.status === status) return;
        const previousOverride = this.#poolStatusOverrides.value.get(poolId);
        const optimistic = new Map<string, PoolStatus>(this.#poolStatusOverrides.value);
        optimistic.set(poolId, status);
        this.#poolStatusOverrides.value = optimistic;
        const version = (this.#poolStatusMutationVersion.get(poolId) ?? 0) + 1;
        this.#poolStatusMutationVersion.set(poolId, version);

        void updatePool({ poolId, request: { active: status === 'active' } }).then(
            (response) => {
                if (this.#poolStatusMutationVersion.get(poolId) !== version) return;
                // Patch the cached pool entries with the server's confirmed
                // `active` value, then drop the override so subsequent
                // pool-list refetches drive the pill directly. Touch both
                // the unscoped (`['lab-pools']`) and the firm-scoped
                // (`['lab-pools', firmId]`) caches so superuser and firm
                // views stay in sync.
                this.#patchPoolInCache(response.pool);
                const cleared = new Map<string, PoolStatus>(this.#poolStatusOverrides.value);
                cleared.delete(poolId);
                this.#poolStatusOverrides.value = cleared;
                this.#markFresh();
                const messageKey =
                    status === 'paused'
                        ? 'admin::pool-management::toast-pool-paused'
                        : 'admin::pool-management::toast-pool-resumed';
                this.#store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t(messageKey, { name: pool.name }),
                        group: 'default',
                        variant: 'success',
                    },
                });
            },
            (error: unknown) => {
                if (this.#poolStatusMutationVersion.get(poolId) !== version) return;
                // The captured `previousOverride` may not match the
                // server truth if earlier in-flight PATCHes for this
                // pool were version-gated out and never wrote it back.
                // Roll the optimistic entry back as a best-effort
                // visual restore, then invalidate the pool-list query
                // so the next refetch reseats `active` from the
                // backend and clears the lingering override.
                const reverted = new Map<string, PoolStatus>(this.#poolStatusOverrides.value);
                if (previousOverride === undefined) reverted.delete(poolId);
                else reverted.set(poolId, previousOverride);
                this.#poolStatusOverrides.value = reverted;
                void invalidatePoolsQuery(this.#queryClient, this.#firmId.value ?? undefined);
                const message = error instanceof Error ? error.message : String(error);
                const errorKey =
                    status === 'paused'
                        ? 'admin::pool-management::toast-pool-pause-error'
                        : 'admin::pool-management::toast-pool-resume-error';
                this.#store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t(errorKey, { name: pool.name, message }),
                        group: 'default',
                        variant: 'error',
                    },
                });
            },
        );
    }

    /**
     * Patch a single pool's entry inside every cached pool-list query
     * (both the unscoped `[LAB_POOLS]` superuser view and any
     * firm-scoped `[LAB_POOLS, firmId]` views). Used by `#setPoolStatus`
     * to fold the server's confirmed `active` value into the cache
     * without triggering a refetch round-trip.
     */
    #patchPoolInCache(updated: InventoryPool): void {
        const matches = this.#queryClient.getQueriesData<ListPoolsResponse>({ queryKey: [LAB_POOLS] });
        for (const [key, data] of matches) {
            if (!data) continue;
            if (!data.pools.some((p) => p.id === updated.id)) continue;
            this.#queryClient.setQueryData<ListPoolsResponse>(key, {
                ...data,
                pools: data.pools.map((p) => (p.id === updated.id ? updated : p)),
            });
        }
    }

    // --- Search commands ---
    setPoolSearchQuery(query: string): void {
        this.#dispatch(setPoolSearchQuery(query));
    }
    setSymbolSearchQuery(query: string): void {
        // Update the input text immediately so typing stays responsive,
        // then commit the backend `symbol` filter on a debounce.
        this.#dispatch(setSymbolSearchQuery(query));
        if (query.trim() === '') {
            // Clearing the box should restore the full (paged) pool right
            // away — there's nothing to debounce.
            this.#applySymbolSearch.cancel();
            this.#dispatch(setAppliedSymbolSearch(''));
            return;
        }
        this.#applySymbolSearch();
    }

    /**
     * Commits the current search box text to the backend `symbol`
     * filter. Debounced so live typing fires a single exact-match
     * request once the user pauses rather than one per keystroke.
     */
    readonly #applySymbolSearch = debounce(() => {
        // Symbols are stored upper-case and the backend filter is a
        // case-sensitive `sym = @sym` match, so normalize the typed term
        // the same way the add-symbol path does before sending it.
        this.#dispatch(setAppliedSymbolSearch(this.#state.value.symbolSearchQuery.trim().toUpperCase()));
    }, SYMBOL_SEARCH_DEBOUNCE_MS);

    // --- Write commands (no-op when read-only) ---
    setAddSymbolDraft(value: string): void {
        if (!this.canWrite.value) return;
        this.#dispatch(setAddSymbolDraft(value));
    }
    addSymbolFromDraft(): void {
        if (!this.canWrite.value) return;
        const draft = this.#state.value.addSymbolDraft.trim();
        if (!draft) return;
        // Only clear the draft when the symbol actually lands. A rejected
        // add (duplicate, no selected pool, …) preserves the user's input
        // so they can correct it instead of silently losing what they typed.
        if (this.#addSymbolInternal(draft)) {
            this.#dispatch(setAddSymbolDraft(''));
        }
    }
    addSymbol(symbol: string): void {
        if (!this.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        this.#addSymbolInternal(symbol);
    }
    #addSymbolInternal(rawSymbol: string): boolean {
        const symbol = rawSymbol.trim().toUpperCase();
        if (!symbol) return false;
        const poolId = this.#state.value.selectedPoolId;
        if (!poolId) return false;
        // De-dupe against both server rows and currently pending adds.
        const serverItems = this.#itemsQuery.signal.value.data?.items ?? [];
        if (serverItems.some((r) => r.symbol === symbol)) return false;
        const existingPending = this.#pendingAddsByPool.value.get(poolId) ?? [];
        if (existingPending.some((r) => r.symbol === symbol)) return false;
        const newRow: InventoryRow = {
            id: `${PENDING_ROW_PREFIX}${poolId}_${symbol.toLowerCase()}_${Date.now()}`,
            symbol,
            currentQuantity: 0,
            currentLabPrice: 0,
            borrowStatus: null,
            quantityUsed: null,
            dailyRevenue: null,
            currentPrice: null,
            fairValuePrice: null,
        };
        const next = new Map(this.#pendingAddsByPool.value);
        // Prepend so the newest add is at the top of the pending block,
        // which (combined with the `[...pending, ...remote]` merge) puts
        // it at row 0 of the table.
        next.set(poolId, [newRow, ...existingPending]);
        this.#pendingAddsByPool.value = next;
        return true;
    }
    editRowQuantity(rowId: string, value: number): void {
        if (!this.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        // Quantities must be non-negative integers: the inventory CSV
        // round-trip only parses integer share counts (see
        // `parseInventoryCsv`), and the table renders them with
        // thousands separators that imply whole shares. Reject
        // fractional / Infinity / NaN inputs so a stray decimal isn't
        // silently truncated on export.
        if (!Number.isInteger(value) || value < 0) return;
        // A value matching the underlying row is a no-op (e.g. focusing
        // then blurring the field without typing). Clear any staged edit
        // instead of staging it, so the row doesn't show as dirty.
        const original = this.serverInventoryRows.value.find((r) => r.id === rowId);
        if (original && original.currentQuantity === value) {
            this.#dispatch(clearRowFieldEdit(rowId, 'currentQuantity'));
            return;
        }
        this.#dispatch(stageRowEdit({ rowId, currentQuantity: value }));
    }
    editRowLabPrice(rowId: string, value: number): void {
        if (!this.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        if (!Number.isFinite(value) || value < 0) return;
        // See `editRowQuantity`: re-applying the row's existing price (the
        // `0`-price focus/blur case) must not dirty the row.
        const original = this.serverInventoryRows.value.find((r) => r.id === rowId);
        if (original && original.currentLabPrice === value) {
            this.#dispatch(clearRowFieldEdit(rowId, 'currentLabPrice'));
            return;
        }
        this.#dispatch(stageRowEdit({ rowId, currentLabPrice: value }));
    }
    clearRowQuantityEdit(rowId: string): void {
        if (!this.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        this.#dispatch(clearRowFieldEdit(rowId, 'currentQuantity'));
    }
    clearRowLabPriceEdit(rowId: string): void {
        if (!this.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        this.#dispatch(clearRowFieldEdit(rowId, 'currentLabPrice'));
    }
    removeRow(rowId: string): void {
        if (!this.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        if (rowId.startsWith(PENDING_ROW_PREFIX)) {
            // It's a locally-added row that never went to the backend; just drop it.
            const poolId = this.#state.value.selectedPoolId;
            if (!poolId) return;
            const pending = this.#pendingAddsByPool.value.get(poolId) ?? [];
            const next = pending.filter((r) => r.id !== rowId);
            const map = new Map(this.#pendingAddsByPool.value);
            if (next.length === 0) map.delete(poolId);
            else map.set(poolId, next);
            this.#pendingAddsByPool.value = map;
            // Drop any staged field edits keyed on this draft id so they
            // don't orphan in `rowEdits` and leave `isDirty` diverging
            // from `unsavedChangeCount`.
            this.#dispatch(clearRowFieldEdit(rowId, 'currentQuantity'));
            this.#dispatch(clearRowFieldEdit(rowId, 'currentLabPrice'));
            return;
        }
        this.#dispatch(stageRowRemoval(rowId));
    }
    restoreRow(rowId: string): void {
        if (!this.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        this.#dispatch(unstageRowRemoval(rowId));
    }
    resetEdits(): void {
        if (!this.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        // The RESET_EDITS reducer case also clears `submitError` so the
        // user isn't stuck on a dead-end error after resetting.
        this.#dispatch(resetEdits());
        const poolId = this.#state.value.selectedPoolId;
        if (poolId && this.#pendingAddsByPool.value.has(poolId)) {
            const map = new Map(this.#pendingAddsByPool.value);
            map.delete(poolId);
            this.#pendingAddsByPool.value = map;
        }
    }
    openSubmitReview(): void {
        if (!this.canWrite.value) return;
        this.#dispatch(openSubmitReview());
    }
    closeSubmitReview(): void {
        this.#dispatch(closeSubmitReview());
    }

    /**
     * Translate staged edits/removals/pending-adds into the per-item
     * backend operations. The locates-ws API doesn't expose a bulk
     * preview/apply, so the model fans out the change set into one
     * `POST .../items` (batched), N `PATCH .../items/{id}`, and N
     * `DELETE .../items/{id}` calls and waits for all to settle.
     *
     * Each op is tagged so a partial failure can leave only the
     * failed pieces staged for retry — succeeded edits/removals are
     * cleared, and pending-adds are dropped only when their batch POST
     * actually committed.
     */
    async submitChanges(): Promise<boolean> {
        if (!this.canWrite.value) return false;
        if (this.#state.value.isSubmitting) return false;
        const poolId = this.#state.value.selectedPoolId;
        if (!poolId) return false;

        const operations = this.#buildSubmitOperations(poolId);
        if (operations.length === 0) {
            this.#dispatch(resetEdits());
            this.#dispatch(closeSubmitReview());
            return true;
        }
        // Only enter the submitting lifecycle once we know there's real
        // work — otherwise the no-op path leaves `isSubmitting` stuck
        // true because nothing dispatches `succeedSubmit`.
        this.#dispatch(startSubmit());
        const totalOps = operations.length;
        const toastId = this.#startActionToast(
            t('admin::pool-management::toast-submit-in-progress', { count: totalOps }),
        );

        try {
            const results = await Promise.allSettled(operations.map((o) => o.promise));
            await Promise.all([
                invalidatePoolItemsQuery(this.#queryClient, poolId),
                invalidatePoolChangeLogQuery(this.#queryClient, poolId),
            ]);
            this.#markFresh();

            // Walk results once, partitioning ops by outcome so partial
            // failures keep only the failed staged changes around.
            const successfulEditIds: string[] = [];
            const successfulRemovalIds: string[] = [];
            let pendingAddsSucceeded = false;
            let firstFailure: unknown = null;
            for (let i = 0; i < operations.length; i++) {
                const op = operations[i]!;
                const res = results[i]!;
                if (res.status === 'fulfilled') {
                    if (op.kind === 'update') successfulEditIds.push(op.rowId);
                    else if (op.kind === 'remove') successfulRemovalIds.push(op.rowId);
                    else {
                        pendingAddsSucceeded = true;
                        // Clear any staged edits keyed by the pending-row ids
                        // so they don't orphan in `rowEdits` once the rows
                        // themselves are gone from `#pendingAddsByPool`.
                        successfulEditIds.push(...op.rowIds);
                    }
                } else if (firstFailure === null) {
                    firstFailure = res.reason;
                }
            }

            if (successfulEditIds.length > 0 || successfulRemovalIds.length > 0) {
                this.#dispatch(clearStagedChanges(successfulEditIds, successfulRemovalIds));
            }
            if (pendingAddsSucceeded) {
                const map = new Map(this.#pendingAddsByPool.value);
                map.delete(poolId);
                this.#pendingAddsByPool.value = map;
            }

            if (firstFailure !== null) {
                const message = firstFailure instanceof Error ? firstFailure.message : String(firstFailure);
                this.#dispatch(failSubmit(message));
                this.#replaceToast(toastId, {
                    variant: 'error',
                    message: t('admin::pool-management::toast-submit-error', { message }),
                });
                return false;
            }

            this.#dispatch(succeedSubmit());
            this.#dispatch(resetEdits());
            this.#dispatch(closeSubmitReview());
            this.#replaceToast(toastId, {
                variant: 'success',
                message: t('admin::pool-management::toast-submit-success', { count: totalOps }),
            });
            return true;
        } catch (e) {
            const message = e instanceof Error ? e.message : t('admin::pool-management::submit-error-generic');
            this.#dispatch(failSubmit(message));
            this.#replaceToast(toastId, {
                variant: 'error',
                message: t('admin::pool-management::toast-submit-error', { message }),
            });
            return false;
        }
    }

    #buildSubmitOperations(poolId: string): SubmitOperation[] {
        const serverItems = this.#itemsQuery.signal.value.data?.items ?? [];
        const itemsById = new Map<string, InventoryItem>();
        for (const item of serverItems) itemsById.set(item.id, item);

        const edits = this.#state.value.rowEdits;
        const removals = new Set(this.#state.value.pendingRowRemovals);
        const pendingAdds = this.#pendingAddsByPool.value.get(poolId) ?? [];

        const ops: SubmitOperation[] = [];

        // PATCH existing items that have staged edits and aren't being removed.
        for (const [rowId, edit] of Object.entries(edits)) {
            if (removals.has(rowId)) continue;
            if (rowId.startsWith(PENDING_ROW_PREFIX)) continue;
            if (!itemsById.has(rowId)) continue;
            const patch: { quantity?: number; price?: number } = {};
            if (edit.currentQuantity !== undefined) patch.quantity = edit.currentQuantity;
            if (edit.currentLabPrice !== undefined) patch.price = edit.currentLabPrice;
            ops.push({ kind: 'update', rowId, promise: updatePoolItem({ poolId, itemId: rowId, request: patch }) });
        }

        // DELETE removed items (skip locally-staged pending adds — they
        // never went to the backend).
        for (const rowId of removals) {
            if (rowId.startsWith(PENDING_ROW_PREFIX)) continue;
            if (!itemsById.has(rowId)) continue;
            ops.push({ kind: 'remove', rowId, promise: removePoolItem({ poolId, itemId: rowId }) });
        }

        // POST any locally pending adds, applying their staged edits.
        if (pendingAdds.length > 0) {
            const newItems: NewPoolItem[] = pendingAdds.map((row) => {
                const edit = edits[row.id];
                return {
                    symbol: row.symbol,
                    quantity: edit?.currentQuantity ?? row.currentQuantity,
                    price: edit?.currentLabPrice ?? row.currentLabPrice,
                };
            });
            ops.push({
                kind: 'add-batch',
                rowIds: pendingAdds.map((row) => row.id),
                promise: addPoolItems({ poolId, request: { items: newItems } }),
            });
        }

        return ops;
    }

    clearSubmitError(): void {
        this.#dispatch(clearSubmitError());
    }

    // --- Upload wizard (lifecycle driven entirely by reducer state) ---
    readonly uploadWizardOpen: ReadonlySignal<boolean> = computed(() => this.#state.value.uploadWizardOpen);
    readonly uploadStep: ReadonlySignal<UploadWizardStep> = computed(() => this.#state.value.uploadStep);
    readonly uploadFile: ReadonlySignal<UploadFileInfo | null> = computed(() => this.#state.value.uploadFile);
    readonly uploadParseResult: ReadonlySignal<InventoryParseResult | null> = computed(
        () => this.#state.value.uploadParseResult,
    );
    readonly uploadCategoryFilter: ReadonlySignal<UploadCategory> = computed(
        () => this.#state.value.uploadCategoryFilter,
    );
    readonly uploadSymbolFilter: ReadonlySignal<string> = computed(() => this.#state.value.uploadSymbolFilter);
    readonly uploadMode: ReadonlySignal<UploadMode> = computed(() => this.#state.value.uploadMode);
    readonly uploadApplying: ReadonlySignal<boolean> = computed(() => this.#state.value.uploadApplying);
    readonly uploadError: ReadonlySignal<string | null> = computed(() => this.#state.value.uploadError);
    readonly uploadAppliedSummary: ReadonlySignal<{ added: number; updated: number; removed: number } | null> =
        computed(() => this.#state.value.uploadAppliedSummary);
    readonly uploadDiff: ReadonlySignal<UploadDiff | null> = computed(() => {
        const parsed = this.#state.value.uploadParseResult;
        if (!parsed) return null;
        // Diff against server rows only; pending local adds don't count as
        // "existing" inventory from the upload's perspective.
        const remote = this.#itemsQuery.signal.value.data?.items.map(fromApiItem) ?? [];
        return diffInventory(parsed.rows, remote);
    });

    openUploadWizard(): void {
        if (!this.canWrite.value) return;
        // OPEN_UPLOAD_WIZARD also clears prior error/summary in the reducer.
        this.#dispatch(openUploadWizard());
    }
    closeUploadWizard(): void {
        // Short-circuit when a real apply is in-flight so the user can't
        // accidentally abandon it; `setFirm` and other forced teardowns
        // dispatch CLOSE_UPLOAD_WIZARD directly to bypass this guard.
        if (this.#state.value.uploadApplying) return;
        this.#dispatch(closeUploadWizard());
    }
    setUploadStep(step: UploadWizardStep): void {
        this.#dispatch(setUploadStep(step));
    }
    setUploadCategoryFilter(category: UploadCategory): void {
        this.#dispatch(setUploadCategoryFilter(category));
    }
    setUploadSymbolFilter(query: string): void {
        this.#dispatch(setUploadSymbolFilter(query));
    }
    setUploadMode(mode: UploadMode): void {
        this.#dispatch(setUploadMode(mode));
    }
    async selectUploadFile(file: File): Promise<void> {
        if (!this.canWrite.value) return;
        this.#dispatch(setUploadError(null));

        const name = file.name.toLowerCase();
        // CSV-only: the parser splits exclusively on commas, so accepting
        // .tsv or other delimited formats would produce confusing "missing
        // column" errors for files that look correct to the user.
        if (!name.endsWith('.csv')) {
            this.#dispatch(setUploadFile(null, null));
            this.#dispatch(
                setUploadError(t('admin::pool-management::upload-error-unsupported-format', { fileName: file.name })),
            );
            return;
        }
        // Hard size cap so a stray multi-hundred-megabyte file can't lock
        // up the main thread inside `file.text()`. 8 MB is well above the
        // 10k-row CSV the wizard advertises (~700 KB) but small enough to
        // parse synchronously without UI jank.
        if (file.size > MAX_UPLOAD_BYTES) {
            this.#dispatch(setUploadFile(null, null));
            this.#dispatch(
                setUploadError(
                    t('admin::pool-management::upload-error-too-large', {
                        limit: `${MAX_UPLOAD_BYTES / 1024 / 1024} MB`,
                    }),
                ),
            );
            return;
        }

        try {
            const text = await file.text();
            const parsed = parseInventoryCsv(text);

            // Reject the file entirely when required columns (Symbol, Quantity)
            // are missing — no point in letting the user proceed without them.
            const headerErrors = parsed.errors.filter((e) => e.rowIndex === 0);
            if (headerErrors.length > 0) {
                this.#dispatch(setUploadFile(null, null));
                this.#dispatch(setUploadError(headerErrors.map((e) => e.message).join(' ')));
                return;
            }

            this.#dispatch(setUploadFile({ name: file.name, size: file.size, uploadedAt: Date.now() }, parsed));
            if (parsed.rows.length === 0) {
                this.#dispatch(setUploadError(t('admin::pool-management::upload-error-no-valid-rows')));
            }
        } catch (e) {
            this.#dispatch(setUploadFile(null, null));
            this.#dispatch(
                setUploadError(e instanceof Error ? e.message : t('admin::pool-management::upload-error-read-failed')),
            );
        }
    }
    clearUploadFile(): void {
        this.#dispatch(setUploadFile(null, null));
        this.#dispatch(setUploadError(null));
    }
    async applyUpload(): Promise<boolean> {
        if (!this.canWrite.value) return false;
        if (this.#state.value.uploadApplying) return false;
        const diff = this.uploadDiff.value;
        const poolId = this.#state.value.selectedPoolId;
        if (!diff || !poolId) return false;

        // Reject the apply when any added row has no LAB Price. The wizard
        // also blocks Apply in this state, but the model has to be the
        // authoritative gate since price-less inventory must never reach
        // the backend.
        if (diff.added.some((item) => item.labPrice === null)) {
            this.#dispatch(setUploadError(t('admin::pool-management::upload-error-missing-price')));
            return false;
        }

        this.#dispatch(startUploadApply());
        // Only `'replace'` mode actually deletes server rows that aren't
        // in the upload; `'merge'` (the only phase-1 mode) treats those
        // rows as kept. `diff.removed` is still computed so the wizard
        // can surface them to the user if a future mode opts in.
        const mode = this.#state.value.uploadMode;
        const removedOpsCount = mode === 'replace' ? diff.removed.length : 0;
        const totalOps = diff.added.length + diff.updated.length + removedOpsCount;
        const toastId = this.#startActionToast(
            t('admin::pool-management::toast-upload-in-progress', { count: totalOps }),
        );
        try {
            const ops: Promise<unknown>[] = [];

            if (diff.added.length > 0) {
                const items: NewPoolItem[] = diff.added.map((item) => ({
                    symbol: item.symbol,
                    quantity: item.quantity,
                    // The missing-price guard above already rejected the apply,
                    // so labPrice is guaranteed non-null at this point.
                    price: item.labPrice!,
                }));
                ops.push(addPoolItems({ poolId, request: { items } }));
            }
            for (const upd of diff.updated) {
                ops.push(
                    updatePoolItem({
                        poolId,
                        itemId: upd.rowId,
                        request: { quantity: upd.toQuantity, price: upd.toLabPrice },
                    }),
                );
            }
            if (mode === 'replace') {
                for (const rem of diff.removed) {
                    ops.push(removePoolItem({ poolId, itemId: rem.rowId }));
                }
            }

            const results = await Promise.allSettled(ops);
            await Promise.all([
                invalidatePoolItemsQuery(this.#queryClient, poolId),
                invalidatePoolChangeLogQuery(this.#queryClient, poolId),
            ]);
            this.#markFresh();
            const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
            if (failures.length > 0) {
                const first = failures[0]!.reason;
                const message = first instanceof Error ? first.message : String(first);
                this.#dispatch(failUploadApply(message));
                this.#replaceToast(toastId, {
                    variant: 'error',
                    message: t('admin::pool-management::toast-upload-error', { message }),
                });
                return false;
            }

            // Upload supersedes any pending local adds for the pool.
            if (this.#pendingAddsByPool.value.has(poolId)) {
                const map = new Map(this.#pendingAddsByPool.value);
                map.delete(poolId);
                this.#pendingAddsByPool.value = map;
            }

            // SUCCEED_UPLOAD_APPLY also sets uploadStep='done' and clears
            // applying/error in one reducer transition. The summary reports
            // what was actually applied; in merge mode `removed` is always
            // 0 because no DELETE was issued.
            this.#dispatch(
                succeedUploadApply({
                    added: diff.added.length,
                    updated: diff.updated.length,
                    removed: removedOpsCount,
                }),
            );
            this.#replaceToast(toastId, {
                variant: 'success',
                message: t('admin::pool-management::toast-upload-success', {
                    added: diff.added.length,
                    updated: diff.updated.length,
                    removed: removedOpsCount,
                }),
            });
            return true;
        } catch (e) {
            const message = e instanceof Error ? e.message : t('admin::pool-management::upload-error-apply-failed');
            this.#dispatch(failUploadApply(message));
            this.#replaceToast(toastId, {
                variant: 'error',
                message: t('admin::pool-management::toast-upload-error', { message }),
            });
            return false;
        }
    }
    downloadInventoryTemplate(): void {
        if (typeof document === 'undefined') return;
        const csv = createInventoryTemplateCsv();
        this.#triggerCsvDownload(csv, 'inventory-template.csv');
    }
    async downloadCurrentPoolCsv(): Promise<void> {
        if (typeof document === 'undefined') return;
        const pool = this.selectedPool.value;
        if (!pool) return;
        // The user expects the full pool, not just the current page.
        // Gather it via real backend pagination; the cache key is shared
        // with the model's "all rows" / substring-search fetches, so this
        // usually completes against an already-warm entry.
        const allOptions = getAllPoolItemsQuery(pool.id);
        const response = await this.#queryClient.fetchQuery({
            ...allOptions,
            queryKey: allOptions.queryKey ?? [],
        });
        // Drop rows the user has staged for removal: keeping them in the
        // export would let them re-upload the file and silently undo the
        // pending delete. Drafts (pending adds + field edits) aren't in
        // the full-pool server response anyway, so this only needs to
        // filter the pending-removal set.
        const pendingRemovals = this.pendingRowRemovals.value;
        const rows = response.items.map(fromApiItem).filter((row) => !pendingRemovals.has(row.id));
        const csv = createInventoryCsv(rows);
        // Filename pattern: `Retail_Pool_2026-04-29.csv`. The shared
        // `sanitizeFilenameSegment` (see top of file) collapses
        // whitespace and strips filesystem-reserved / control
        // characters (Windows is the strictest with `<>:"/\|?*`) so
        // the download dialog stays single-token. Date is the local
        // ISO yyyy-mm-dd so two exports on the same day overwrite
        // cleanly in the Downloads dir.
        const safeName = sanitizeFilenameSegment(pool.name) || 'pool';
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        this.#triggerCsvDownload(csv, `${safeName}_${yyyy}-${mm}-${dd}.csv`);
    }

    downloadChangeLogCsv(): void {
        if (typeof document === 'undefined') return;
        const pool = this.selectedPool.value;
        if (!pool) return;
        const entries = this.changeLogEntries.value;
        if (entries.length === 0) return;
        // `changeLogEntries` is the model's filtered + sorted list; the
        // tab additionally groups it by local day for the timeline UI
        // but the CSV doesn't carry the grouping (consumers can sort
        // the spreadsheet themselves). Exporting the visible filtered
        // set means the user gets what they're looking at, not the
        // full server log; centralizing the build here keeps any
        // future toolbar consumer from producing a subtly different
        // CSV shape.
        const csv = createChangeLogCsv(entries, {
            columnLabels: {
                timestamp: t('admin::pool-management::changelog-export-col-timestamp'),
                user: t('admin::pool-management::changelog-export-col-user'),
                symbol: t('admin::pool-management::changelog-export-col-symbol'),
                direction: t('admin::pool-management::changelog-export-col-direction'),
                quantity: t('admin::pool-management::changelog-export-col-quantity'),
                price: t('admin::pool-management::changelog-export-col-price'),
                orderId: t('admin::pool-management::changelog-export-col-order-id'),
            },
        });
        const safeName = sanitizeFilenameSegment(pool.name) || 'pool';
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        this.#triggerCsvDownload(csv, `${safeName}_change-log_${yyyy}-${mm}-${dd}.csv`);
    }

    #triggerCsvDownload(csv: string, filename: string): void {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        // Defer revocation: synchronous revoke can race with the browser's
        // download initiation in Firefox and Safari and abort the download.
        setTimeout(() => URL.revokeObjectURL(url), 0);
    }

    async refresh(): Promise<void> {
        const firmId = this.#firmId.value;
        await invalidatePoolsQuery(this.#queryClient, firmId ?? undefined);
        const poolId = this.#state.value.selectedPoolId;
        if (poolId) {
            await Promise.all([
                invalidatePoolItemsQuery(this.#queryClient, poolId),
                invalidatePoolChangeLogQuery(this.#queryClient, poolId),
            ]);
        }
        this.#markFresh();
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
